<template>
  <section class="section">
    <section class="container is-max-widescreen">
      <h1 class="title has-text-centered">Migration de tâches entre salariés</h1>

      <!-- Bandeau explicatif -->
      <div class="box">
        <h2 class="subtitle">
          <b-icon icon="info-circle" size="is-small" class="mr-1"></b-icon>
          Comment ça fonctionne
        </h2>
        <p class="mb-4">
          Cet outil transfère les tâches d'un salarié vers un autre, en conservant l'historique.
          <br>Les tâches de l'ancien salarié sont archivées (jamais supprimées) et de nouvelles tâches
          sont créées pour le nouveau salarié.
        </p>

        <div class="columns">
          <div class="column is-7">
            <article class="message migration-prorata">
              <div class="message-header">
                <p>Calcul des jours</p>
              </div>
              <div class="message-body">
                <p>Indiquez la <strong>date d'arrivée du nouveau salarié</strong> (date de bascule).
                  L'outil calcule automatiquement combien de jours restent à faire sur chaque tâche,
                  et crée de nouvelles tâches pour le remplaçant avec <strong>le nombre de jours
                  restants</strong>.</p>
                <p class="mt-3"><strong>Exemple :</strong> A avait 20 jours du 01/01 au 31/12/2026.
                  B arrive le 01/07 &rarr; la moitié du temps est écoulée &rarr; B récupère
                  <strong>10 jours</strong> (du 01/07 au 31/12).</p>
                <p class="mt-3 is-size-7 has-text-grey">
                  <b-icon icon="euro-sign" size="is-small" class="mr-1"></b-icon>
                  <strong>Coût jour :</strong> les tâches non encore commencées sont transférées
                  au <strong>coût jour du nouveau salarié</strong>. Les tâches partiellement
                  réalisées conservent le coût jour de l'ancien (budget initial).
                </p>
                <p class="mt-2 is-size-7 has-text-grey">
                  Les tâches déjà terminées avant l'arrivée du nouveau salarié sont ignorées.
                  Celles qui n'ont pas encore commencé sont transférées intégralement.
                </p>
              </div>
            </article>
          </div>
          <div class="column is-5">
            <article class="message migration-example">
              <div class="message-header">
                <p>Exemple pluriannuel</p>
              </div>
              <div class="message-body">
                <p class="mb-3">B arrive le 03/11/2025 et reprend les projets de A&nbsp;:</p>
                <table class="table is-narrow is-bordered is-size-7 is-fullwidth">
                  <thead><tr><th>Année</th><th>Salarié A</th><th>Salarié B</th></tr></thead>
                  <tbody>
                    <tr><td>2024</td><td>Archivé (historique intact)</td><td>Rien (pas en poste)</td></tr>
                    <tr><td>2025</td><td>Archivé (janv. &rarr; oct.)</td><td>Prorata (nov. &rarr; déc.)</td></tr>
                    <tr><td>2026</td><td>Archivé</td><td>Intégralité des jours</td></tr>
                  </tbody>
                </table>
              </div>
            </article>
          </div>
        </div>

        <details class="mt-2">
          <summary class="has-text-weight-semibold is-size-6 mb-2" style="cursor: pointer;">
            Quels projets sont pris en compte ?
          </summary>
          <div class="content mt-2">
            <p>La migration ne traite que les projets <strong>actifs et imputables</strong>. Sont automatiquement exclus :</p>
            <ul>
              <li><strong>Projets «&nbsp;Charge uniquement&nbsp;»</strong> (reports comptables, 99MARGE)&nbsp;:
                les migrer créerait des doublons.</li>
              <li><strong>Projets non validés dans ERP+</strong>&nbsp;:
                pas injectés dans Axelor, aucun temps imputé.</li>
              <li><strong>Projets clôturés dans Axelor</strong> (aucune phase imputable).</li>
            </ul>
          </div>
        </details>
      </div>

      <hr>

      <!-- Formulaire -->
      <div class="box">
        <h2 class="subtitle">Paramètres de la migration</h2>

        <div class="columns">
          <div class="column is-half">
            <b-field label="Ancien salarié (source)">
              <b-autocomplete
                  v-model="fromInput"
                  :data="filteredFromList"
                  placeholder="Nom du salarié à remplacer"
                  icon="user-minus"
                  field="name"
                  clearable
                  @select="option => fromSelected = option">
                <template #empty>Pas de résultats</template>
                <template slot-scope="props">
                  <div>
                    {{ props.option.name }}
                    <small class="has-text-grey"> - {{ props.option.service }}</small>
                  </div>
                </template>
              </b-autocomplete>
            </b-field>
          </div>

          <div class="column is-half">
            <b-field label="Nouveau salarié (cible)">
              <b-autocomplete
                  v-model="toInput"
                  :data="filteredToList"
                  placeholder="Nom du salarié qui reprend"
                  icon="user-plus"
                  field="name"
                  clearable
                  @select="option => toSelected = option">
                <template #empty>Pas de résultats</template>
                <template slot-scope="props">
                  <div>
                    {{ props.option.name }}
                    <small class="has-text-grey"> - {{ props.option.service }}</small>
                  </div>
                </template>
              </b-autocomplete>
            </b-field>
          </div>
        </div>

        <div class="columns">
          <div class="column is-one-third">
            <b-field label="Date de bascule (arrivée du nouveau)" grouped>
              <b-select v-model="switchDay" placeholder="Jour">
                <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
              </b-select>
              <b-select v-model="switchMonth" placeholder="Mois" expanded>
                <option v-for="(m, i) in months" :key="i" :value="i + 1">{{ m }}</option>
              </b-select>
              <b-select v-model="switchYear" placeholder="Année">
                <option v-for="y in yearRange" :key="y" :value="y">{{ y }}</option>
              </b-select>
            </b-field>
          </div>

        </div>

        <div class="columns">
          <div class="column is-one-third">
            <b-field label="Filtrer par projet (optionnel)">
              <b-input
                  v-model="projectFilter"
                  placeholder="ex: 64DOMOND_26"
                  icon="filter">
              </b-input>
            </b-field>
          </div>
        </div>

        <div class="buttons">
          <b-button
              class="btn-simulate"
              icon-left="eye"
              :loading="isLoading"
              :disabled="!canSubmit"
              @click="preview">
            Simuler la migration
          </b-button>
        </div>

        <!-- Indicateur de scroll vers les résultats -->
        <div v-if="result" class="has-text-centered scroll-hint" @click="scrollToResult">
          <b-icon icon="arrow-down" size="is-medium"></b-icon>
          <p>Voir le résultat ci-dessous</p>
        </div>
      </div>

      <!-- Résultats de la simulation -->
      <div class="box" v-if="result" ref="resultBox">
        <div class="level mb-4">
          <div class="level-left">
            <h2 class="subtitle mb-0">
              <b-icon icon="clipboard-check" size="is-small"></b-icon>
              Rapport {{ result.dryRun ? 'de simulation' : 'd\'exécution' }}
            </h2>
          </div>
          <div class="level-right">
            <b-button
                class="btn-export"
                icon-left="download"
                @click="exportCSV">
              Exporter le rapport (CSV)
            </b-button>
          </div>
        </div>

        <!-- Phrase récap -->
        <div class="notification is-light mb-4">
          <p class="has-text-centered is-size-5">
            <strong>{{ result.from }}</strong> &rarr; <strong>{{ result.to }}</strong>
          </p>
          <p class="has-text-centered" v-if="result.tasksArchived > 0">
            {{ result.tasksArchived }} tâche(s) archivée(s),
            {{ result.tasksCreated }} tâche(s) créée(s) pour {{ result.to }}
          </p>
        </div>

        <!-- Compteurs -->
        <div class="columns">
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Documents modifiés</p>
              <p class="title">{{ result.docsModified }}</p>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Tâches archivées</p>
              <p class="title">{{ result.tasksArchived }}</p>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Tâches créées</p>
              <p class="title">{{ result.tasksCreated }}</p>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Répartition des marges</p>
              <p class="title is-6">{{ ventilationLabel }}</p>
              <p class="is-size-7 has-text-grey mt-1" v-if="result.ventilationStatus === 'created'">
                Une ligne sera créée pour {{ result.to }} dans la ventilation des marges
              </p>
              <p class="is-size-7 has-text-grey mt-1" v-else-if="result.ventilationStatus === 'exists'">
                {{ result.to }} a déjà une ligne dans la ventilation des marges
              </p>
              <p class="is-size-7 has-text-grey mt-1" v-else-if="result.ventilationStatus === 'skip-stagiaire'">
                Les stagiaires n'ont pas de ventilation des marges
              </p>
            </div>
          </div>
        </div>

        <!-- Statut référentiel salarié par année -->
        <div class="notification is-info is-light mb-4" v-if="result.byYear && result.byYear.some(y => y.salariesStatus === 'added')">
          <p>
            <b-icon icon="user-plus" size="is-small"></b-icon>
            <strong>{{ result.to }}</strong> — ajout au référentiel :
          </p>
          <div class="mt-2">
            <span v-for="ys in result.byYear" :key="ys.year" class="tag is-medium mr-2 mb-1"
                  :class="salariesTagClass(ys)">
              <strong class="mr-1">{{ ys.year }}</strong> :
              <span v-if="ys.salariesStatus === 'added'" class="ml-1">absent → sera ajouté (salaries + marges)</span>
              <span v-else-if="ys.salariesStatus === 'unchanged' && ys.tasksCreated > 0" class="ml-1">déjà présent</span>
              <span v-else class="ml-1">aucune tâche migrée</span>
            </span>
          </div>
        </div>

        <!-- Résumé par année -->
        <div class="mb-4" v-if="yearSummary.length > 0">
          <h3 class="subtitle is-6 mb-2">Résumé par année</h3>
          <div class="tags">
            <span class="tag is-medium" v-for="ys in yearSummary" :key="ys.year">
              <strong class="mr-1">{{ ys.year }}</strong> :
              <span v-if="ys.transferred > 0" class="ml-1">{{ ys.transferred }} transférée(s)</span>
              <span v-if="ys.transferred > 0 && ys.skipped > 0">,</span>
              <span v-if="ys.skipped > 0" class="ml-1">{{ ys.skipped }} ignorée(s)</span>
            </span>
          </div>
        </div>

        <!-- Légende des couleurs -->
        <div class="legend mb-4">
          <span class="legend-item">
            <span class="tag tag-prorata is-small">Prorata</span>
            <small>Jours restants calculés et transférés</small>
          </span>
          <span class="legend-item">
            <span class="tag tag-integral is-small">Transfert intégral</span>
            <small>100% des jours transférés (tâche après la bascule)</small>
          </span>
          <span class="legend-item">
            <span class="tag tag-skip is-small">Ignoré</span>
            <small>Tâche terminée avant la bascule, non touchée</small>
          </span>
        </div>

        <!-- Tableau de détail -->
        <b-table
            v-if="result.details && result.details.length > 0"
            :data="result.details"
            :paginated="result.details.length > 20"
            :per-page="30"
            striped
            narrowed
            default-sort="year">
          <b-table-column field="year" label="Année" centered sortable v-slot="props">
            <b-tag>{{ props.row.year }}</b-tag>
          </b-table-column>
          <b-table-column field="docId" label="Projet" sortable v-slot="props">
            {{ props.row.docId }}
          </b-table-column>
          <b-table-column field="salarie" label="Tâche" v-slot="props">
            {{ props.row.salarie }}
          </b-table-column>
          <b-table-column field="oldDays" label="Jours initiaux" centered v-slot="props">
            {{ props.row.oldDays }}
          </b-table-column>
          <b-table-column field="consumedDays" label="Jours consommés (A)" centered v-slot="props">
            <span v-if="props.row.consumedDays !== undefined">{{ props.row.consumedDays }}</span>
            <span v-else-if="props.row.status === 'skip'" class="has-text-grey is-size-7">Terminée</span>
            <span v-else-if="props.row.prorataDetail" class="has-text-grey is-size-7">
              &lt; 0.5j
              <b-tooltip position="is-right" multilined type="is-dark">
                <template v-slot:content>
                  <strong>Pourquoi transfert intégral ?</strong><br>
                  Tâche du {{ formatDateFR(props.row.prorataDetail.taskStart) }} au {{ formatDateFR(props.row.prorataDetail.taskEnd) }}
                  ({{ props.row.prorataDetail.totalCalendarDays }} jours).<br>
                  Bascule après {{ props.row.prorataDetail.elapsedDays }} jour(s)
                  → prorata = {{ props.row.prorataDetail.rawConsumed }} jour(s) consommé(s).<br>
                  En dessous de 0.5 jour, la tâche est transférée intégralement.
                </template>
                <b-icon icon="info-circle" size="is-small" class="has-text-info" style="cursor: help;"></b-icon>
              </b-tooltip>
            </span>
            <span v-else class="has-text-grey is-size-7">0 (intégral)</span>
          </b-table-column>
          <b-table-column field="newDays" label="Jours repris (B)" centered v-slot="props">
            <span :class="{'has-text-grey': props.row.status === 'skip'}">
              {{ props.row.newDays !== null ? props.row.newDays : '—' }}
            </span>
          </b-table-column>
          <b-table-column field="action" label="Action" centered v-slot="props">
            <span class="tag" :class="actionTagClass(props.row)">
              {{ actionLabel(props.row) }}
            </span>
          </b-table-column>
        </b-table>

        <!-- Bouton d'exécution -->
        <div class="buttons mt-4" v-if="result.dryRun && result.tasksCreated > 0">
          <b-button
              type="is-danger"
              icon-left="check-circle"
              :loading="isExecuting"
              @click="confirmExecute">
            Exécuter la migration
          </b-button>
          <p class="help is-danger">
            Cette opération va archiver {{ result.tasksArchived }} tâche(s)
            et en créer {{ result.tasksCreated }} nouvelle(s). Cette action est irréversible.
          </p>
        </div>

        <!-- Message si aucune tâche -->
        <div v-if="result.tasksCreated === 0 && result.tasksArchived === 0" class="notification is-warning">
          <span v-if="projectFilter">
            Le projet "{{ projectFilter }}" n'a pas de tâche assignée à "{{ result.from }}".
          </span>
          <span v-else>
            Aucune tâche trouvée pour "{{ result.from }}". Vérifiez le nom exact du salarié.
          </span>
        </div>

        <!-- Confirmation exécution réussie -->
        <div v-if="!result.dryRun && result.success" class="notification is-success mt-4">
          Migration terminée avec succès. {{ result.tasksArchived }} tâche(s) archivée(s),
          {{ result.tasksCreated }} tâche(s) créée(s) pour {{ result.to }}.
        </div>

        <!-- Retrait du salarié source du référentiel (point 8) -->
        <div v-if="!result.dryRun && result.success && result.sourceRemoval" class="box mt-4">
          <h3 class="subtitle is-6">
            <b-icon icon="user-minus" size="is-small" class="mr-1"></b-icon>
            Retirer «&nbsp;{{ result.from }}&nbsp;» du référentiel
          </h3>

          <!-- Encore des tâches actives → message explicatif -->
          <div v-if="!result.sourceRemoval.canRemove" class="notification is-warning">
            <p>«&nbsp;{{ result.from }}&nbsp;» a encore des tâches actives sur cette base
              et ne peut pas être retiré pour le moment :</p>
            <ul class="mt-2">
              <li v-for="t in result.sourceRemoval.remainingTasks" :key="t.docId">
                {{ t.docId }} ({{ t.count }} tâche(s))
              </li>
            </ul>
          </div>

          <!-- Plus de tâches → proposition de suppression -->
          <div v-if="result.sourceRemoval.canRemove && !sourceRemoved">
            <p class="mb-3">
              «&nbsp;{{ result.from }}&nbsp;» n'a plus aucune tâche active sur cette base.
              Souhaitez-vous le retirer du référentiel salariés&nbsp;?
            </p>
            <p class="mb-3 is-size-7 has-text-grey">
              Cette suppression ne concerne que l'année en cours. Le salarié reste
              présent dans les référentiels des années précédentes.
            </p>
            <div class="buttons">
              <b-button
                  type="is-warning"
                  icon-left="trash"
                  :loading="isRemovingSource"
                  @click="removeSource">
                Oui, retirer
              </b-button>
              <b-button @click="result.sourceRemoval = null">
                Non, conserver
              </b-button>
            </div>
          </div>

          <!-- Confirmation de suppression -->
          <div v-if="sourceRemoved" class="notification is-success">
            «&nbsp;{{ result.from }}&nbsp;» a été retiré du référentiel salariés de cette base.
          </div>
        </div>
      </div>

    </section>
  </section>
</template>

<script>
export default {
  name: "MigrationSalaries",
  mounted() {
    this.loadSalaries();
  },
  computed: {
    filteredFromList() {
      return this.salariesList.filter(s =>
          s.name.toLowerCase().indexOf(this.fromInput.toLowerCase()) >= 0
      );
    },
    filteredToList() {
      return this.salariesList.filter(s =>
          s.name.toLowerCase().indexOf(this.toInput.toLowerCase()) >= 0
      );
    },
    canSubmit() {
      if (!this.fromSelected || !this.toSelected) return false;
      if (this.fromSelected.name === this.toSelected.name) return false;
      if (!this.switchDateStr) return false;
      return true;
    },
    switchDateStr() {
      if (!this.switchDay || !this.switchMonth || !this.switchYear) return '';
      const m = String(this.switchMonth).padStart(2, '0');
      const d = String(this.switchDay).padStart(2, '0');
      return this.switchYear + '-' + m + '-' + d;
    },
    ventilationLabel() {
      if (!this.result) return '';
      switch (this.result.ventilationStatus) {
        case 'created': return 'Créée';
        case 'exists': return 'Déjà présente';
        case 'skip-stagiaire': return 'Non nécessaire';
        case 'unchanged': return 'Inchangée';
        default: return this.result.ventilationStatus;
      }
    },
    months() {
      return ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
              'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    },
    yearRange() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current - 3; y <= current + 2; y++) years.push(y);
      return years;
    },
    yearSummary() {
      if (!this.result || !this.result.details) return [];
      const map = {};
      this.result.details.forEach(d => {
        const y = d.year || '?';
        if (!map[y]) map[y] = { year: y, transferred: 0, skipped: 0 };
        if (d.status === 'skip') {
          map[y].skipped++;
        } else {
          map[y].transferred++;
        }
      });
      return Object.values(map).sort((a, b) => a.year - b.year);
    }
  },
  methods: {
    formatDateFR(dateStr) {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length !== 3) return dateStr;
      return parts[2] + '/' + parts[1] + '/' + parts[0];
    },
    loadSalaries() {
      this.$axios.get('migration/salaries')
          .then(res => {
            this.salariesList = res.data.salaries;
          })
          .catch(err => {
            this.$buefy.toast.open({
              message: 'Erreur de chargement des salariés : ' + (err.response ? err.response.data.error : err.message),
              type: 'is-danger'
            });
          });
    },
    actionLabel(row) {
      if (row.status === 'skip') return 'Ignoré (terminée)';
      switch (row.action) {
        case 'prorata': return 'Prorata';
        case 'prorata-full': return 'Transfert intégral';
        default: return row.status;
      }
    },
    actionTagClass(row) {
      if (row.status === 'skip') return 'tag-skip';
      switch (row.action) {
        case 'prorata': return 'tag-prorata';
        case 'prorata-full': return 'tag-integral';
        default: return 'is-light';
      }
    },
    salariesTagClass(ys) {
      if (ys.salariesStatus === 'added') return 'is-success is-light';
      if (ys.salariesStatus === 'unchanged' && ys.tasksCreated > 0) return 'is-info is-light';
      return 'is-light';
    },
    scrollToResult() {
      this.$nextTick(() => {
        if (this.$refs.resultBox) {
          this.$refs.resultBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    exportCSV() {
      if (!this.result || !this.result.details) return;
      const sep = ';';
      const headers = ['Année', 'Projet', 'Tâche', 'Jours initiaux', 'Jours consommés', 'Jours repris', 'Action', 'Statut'];

      const rows = this.result.details.map(d => {
        return [
          d.year || '',
          d.docId || '',
          d.salarie || '',
          d.oldDays !== undefined ? d.oldDays : '',
          d.consumedDays !== undefined ? d.consumedDays : '',
          d.newDays !== null && d.newDays !== undefined ? d.newDays : '',
          this.actionLabel(d),
          d.status || ''
        ];
      });

      // Ligne résumé
      const summary = ['', 'TOTAL', '', '', '', 'Archivées: ' + this.result.tasksArchived + ' / Créées: ' + this.result.tasksCreated, '', ''];

      let csv = '\uFEFF'; // BOM UTF-8 pour Excel
      csv += 'Rapport de migration - ' + this.result.from + ' vers ' + this.result.to + '\n';
      if (this.switchDateStr) csv += 'Date de bascule: ' + this.switchDateStr + '\n';
      csv += 'Date du rapport: ' + new Date().toLocaleDateString('fr-FR') + '\n';
      csv += '\n';
      csv += headers.join(sep) + '\n';
      rows.forEach(r => { csv += r.join(sep) + '\n'; });
      csv += summary.join(sep) + '\n';

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'migration_' + this.result.from.replace(/\s+/g, '_') + '_vers_' + this.result.to.replace(/\s+/g, '_') + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
    preview() {
      this.isLoading = true;
      this.result = null;

      const params = {
        from: this.fromSelected.name,
        to: this.toSelected.name,
        mode: 'prorata',
        date: this.switchDateStr || null,
        project: this.projectFilter || null
      };

      this.$axios.post('migration/preview', params)
          .then(res => {
            this.result = res.data;
            this.scrollToResult();
          })
          .catch(err => {
            this.$buefy.toast.open({
              message: 'Erreur : ' + (err.response ? err.response.data.error : err.message),
              type: 'is-danger'
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    confirmExecute() {
      this.$buefy.dialog.confirm({
        title: 'Confirmer la migration',
        message: `
          <p>Vous êtes sur le point de :</p>
          <ul>
            <li>Archiver <strong>${this.result.tasksArchived}</strong> tâche(s) de <strong>${this.result.from}</strong></li>
            <li>Créer <strong>${this.result.tasksCreated}</strong> tâche(s) pour <strong>${this.result.to}</strong></li>
          </ul>
          <p class="mt-2"><strong>Cette action est irréversible.</strong></p>`,
        confirmText: 'Oui, exécuter',
        cancelText: 'Annuler',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.execute()
      });
    },
    execute() {
      this.isExecuting = true;

      const params = {
        from: this.fromSelected.name,
        to: this.toSelected.name,
        mode: 'prorata',
        date: this.switchDateStr || null,
        project: this.projectFilter || null
      };

      this.$axios.post('migration/execute', params)
          .then(res => {
            this.result = res.data;
            this.$buefy.toast.open({
              message: 'Migration effectuée avec succès !',
              type: 'is-success'
            });
          })
          .catch(err => {
            this.$buefy.toast.open({
              message: 'Erreur : ' + (err.response ? err.response.data.error : err.message),
              type: 'is-danger'
            });
          })
          .finally(() => {
            this.isExecuting = false;
          });
    },
    removeSource() {
      this.isRemovingSource = true;
      this.$axios.post('migration/remove-source', { salarie: this.result.from })
          .then(() => {
            this.sourceRemoved = true;
            this.$buefy.toast.open({
              message: '«\u00a0' + this.result.from + '\u00a0» retiré du référentiel.',
              type: 'is-success'
            });
          })
          .catch(err => {
            this.$buefy.toast.open({
              message: 'Erreur : ' + (err.response ? err.response.data.error : err.message),
              type: 'is-danger'
            });
          })
          .finally(() => {
            this.isRemovingSource = false;
          });
    }
  },
  data() {
    return {
      salariesList: [],
      fromInput: '',
      toInput: '',
      fromSelected: null,
      toSelected: null,
      mode: 'prorata',
      switchDay: null,
      switchMonth: null,
      switchYear: null,
      projectFilter: '',
      isLoading: false,
      isExecuting: false,
      isRemovingSource: false,
      sourceRemoved: false,
      result: null
    }
  }
}
</script>

<style scoped>
.migration-prorata .message-header {
  background-color: #335280;
  color: white;
}
.migration-prorata .message-body {
  border-color: #335280;
}
.migration-example .message-header {
  background-color: #E95420;
  color: white;
}
.migration-example .message-body {
  border-color: #E95420;
}
.btn-simulate {
  background-color: #7B2D8E !important;
  color: white !important;
  border: none !important;
  font-weight: 600;
  padding-left: 1.5em;
  padding-right: 1.5em;
}
.btn-simulate:hover {
  background-color: #5E2170 !important;
}
.scroll-hint {
  margin-top: 0.75rem;
  color: #335280;
  cursor: pointer;
  animation: bounce 1.5s ease infinite;
}
.scroll-hint:hover {
  color: #264060;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
}

/* Bouton export */
.btn-export {
  background-color: #335280 !important;
  color: white !important;
  border: none !important;
  font-weight: 600;
}
.btn-export:hover {
  background-color: #264060 !important;
}

/* Tags personnalisés pour les actions */
.tag-prorata {
  background-color: #48c774 !important;
  color: white !important;
}
.tag-integral {
  background-color: #335280 !important;
  color: white !important;
}
.tag-skip {
  background-color: #FFD700 !important;
  color: #333 !important;
}

/* Légende */
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.legend-item small {
  color: #666;
}
</style>
